import Component from '@glimmer/component';
import { t } from 'ember-intl';
import Drawer from 'tio-reimagined/components/drawer';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import InlineDocumentUpload from 'tio-employee/components/reimagined/inline-document-upload';
import { PhCloudArrowUp } from 'ember-phosphor-icons';
import type PersonModel from 'tio-common/models/person';

/**
 * DocumentUploadDrawer — Slide-out drawer wrapping InlineDocumentUpload.
 *
 * Opened when the user clicks "update your loan information" on the
 * contribution history page.
 *
 * @example
 * ```gts
 * <DocumentUploadDrawer
 *   @isOpen={{this.isUploadDrawerOpen}}
 *   @onClose={{this.toggleUploadDrawer}}
 *   @person={{this.person}}
 * />
 * ```
 */
interface DocumentUploadDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** Person whose documents are being uploaded */
    person: PersonModel;
  };
  Element: HTMLDivElement;
}

export default class DocumentUploadDrawer extends Component<DocumentUploadDrawerSignature> {
  <template>
    <Drawer @isOpen={{@isOpen}} @onClose={{@onClose}} @size="lg" @placement="right" ...attributes>
      <:header>
        <div class="flex items-center gap-3">
          <div class="p-2 bg-indigo-100 rounded-lg">
            <PhCloudArrowUp @size={{20}} class="text-indigo-600" />
          </div>
          <div>
            <Heading @level={{3}} class="text-lg font-semibold text-gray-900">
              {{t "slr.reimagined.upload_loan_info_title"}}
            </Heading>
            <Text class="text-sm text-gray-500">
              {{t "slr.reimagined.upload_loan_info_subtitle"}}
            </Text>
          </div>
        </div>
      </:header>
      <:body>
        <InlineDocumentUpload @person={{@person}} />
      </:body>
    </Drawer>
  </template>
}
